import { useLocation, useNavigate } from "react-router-dom";
import {
  REVIEWER_EDIT_APPLICATION_ROUTE,
  canRoleEditApplications,
  getReviewerEditLock,
} from "../../config/reviewerApplicationEdit";
import { useUser } from "../../contexts/UserContext";

/**
 * The "Edit" button on a reviewer's application details screen. Shared by
 * the Level 1, 2 and 3 details screens so the rule for showing and enabling
 * it lives in one place.
 *
 * Renders nothing for a role that cannot edit applications (for example the
 * Accountant, who opens the same details screen) or while the feature flag
 * is off. When the reviewer's role can edit but this application is closed
 * to editing, the button is shown disabled with the reason as its tooltip.
 */
export function ReviewerEditButton({
  application,
  className,
}: {
  application:
    | ({ id?: number; status?: string } & Record<string, unknown>)
    | null
    | undefined;
  className?: string;
}) {
  const user = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  if (!application?.id || !canRoleEditApplications(user?.role)) return null;

  const lockReason = getReviewerEditLock(application, user?.role);

  return (
    <button
      type="button"
      disabled={!!lockReason}
      title={lockReason || ""}
      onClick={() =>
        navigate(REVIEWER_EDIT_APPLICATION_ROUTE, {
          state: { id: application.id, returnTo: location.pathname },
        })
      }
      className={
        className ??
        "bg-primary px-6 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
      }
    >
      Edit
    </button>
  );
}
